import React, { useEffect, useRef, useState } from 'react';
import { 
    ConnectTacoWebSocket, 
    SetTacoSocketMessageHandler, 
    ClearTacoSocketMessageHandler, 
    DisconnectTacoWebSocket, 
    SendTacoTaps 
} from '../../../utilities/BL/Tacos';
import { GoalBase } from '../../Admin/Commons/GoalBase';

/**
 * COMPONENTE AFICIONADO: TACO TAP CHALLENGE
 */
const Tacos = () => {
    // ==========================================================================
    // ESTADOS DE JUGADOR Y RED
    // ==========================================================================
    const [playerId, setPlayerId] = useState(() => sessionStorage.getItem('taco.player') || '');
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [toastMessage, setToastMessage] = useState('');

    // ==========================================================================
    // ESTADOS DEL JUEGO (Sincronizados con el Servidor)
    // ==========================================================================
    const [matchState, setMatchState] = useState({
        matchId: null,
        phase: 'WAITING', // WAITING, RUNNING, FINISHED
        winner: 'none'
    });

    // Estados visuales locales (para respuesta inmediata)
    const [localScore, setLocalScore] = useState(0);
    const [localStep, setLocalStep] = useState(1);
    
    // Referencias de alto rendimiento
    const tacoImageRef = useRef(null);
    const tapBufferRef = useRef({ count: 0, timer: null, isSending: false });

    // Configuración
    const BATCH_SIZE = 5;
    const FLUSH_MS = 120;
    const MAXIMUM_TACO_STEPS = 4;
    const FLOATING_EMOJIS = ['🌮', '🔥', '✨', '👨‍🍳', '🌶️', '🍅'];
    
    // ==========================================================================
    // RUTAS DINÁMICAS
    // Dependiendo de quién se conectó, pintamos a su personaje
    // ==========================================================================
    const ASSETS = {
        character: playerId === 'player2' ? '/img/games/taco/tunita-taquera.png' : '/img/games/taco/lucho-taquero.png',
        tacoStepTemplate: (stepNumber) => `/img/games/taco/${stepNumber}.png` 
    };

    // ==========================================================================
    // CONEXIÓN WEBSOCKET
    // ==========================================================================
    useEffect(() => {
        if (!playerId) return;

        const showToast = (m) => { setToastMessage(m); setTimeout(() => setToastMessage(''), 2500); };
        
        ConnectTacoWebSocket(playerId, setConnectionStatus, showToast).then(() => {
            SetTacoSocketMessageHandler(handleIncomingMessage);
        });

        return () => {
            ClearTacoSocketMessageHandler();
            DisconnectTacoWebSocket();
            if (tapBufferRef.current.timer) clearTimeout(tapBufferRef.current.timer);
        };
    }, [playerId]);

    const handleIncomingMessage = (msg) => {
        const type = (msg?.type || '').toLowerCase();
        const payload = msg?.payload || {};

        if (type === 'taco.progress') {
            setMatchState(prev => ({
                ...prev,
                matchId: payload.matchId,
                phase: payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING',
                winner: payload.winner
            }));

            // Sincronizamos la UI local con la verdad del servidor
            const myStats = playerId === 'player1' ? payload.player1 : payload.player2;
            if (myStats) {
                setLocalScore(myStats.tacosCompleted);
                setLocalStep(myStats.currentStep);
            }
        } else if (type === 'taco.finished') {
            setMatchState(prev => ({
                ...prev,
                phase: 'FINISHED',
                winner: payload.winner
            }));
        }
    };

    // ==========================================================================
    // SISTEMA DE BATCHING (Envío optimizado de Taps)
    // ==========================================================================
    const canTap = Boolean(playerId && matchState.matchId && matchState.phase === 'RUNNING' && matchState.winner === 'none');

    const flushTapsToServer = async () => {
        const buffer = tapBufferRef.current;
        if (buffer.timer) { clearTimeout(buffer.timer); buffer.timer = null; }
        if (buffer.isSending || buffer.count <= 0 || !matchState.matchId) return;

        const tapsToSend = buffer.count;
        buffer.count = 0;
        buffer.isSending = true;

        try {
            await SendTacoTaps({ matchId: matchState.matchId, playerId, taps: tapsToSend });
        } catch {
            buffer.count += tapsToSend; // Si falla, los regresamos a la cola
        } finally {
            buffer.isSending = false;
        }
    };

    // ==========================================================================
    // LÓGICA DE PARTÍCULAS VISUALES (DOM Directo)
    // ==========================================================================
    const spawnFloatingEmoji = (x, y) => {
        const emoji = document.createElement('div');
        emoji.className = 'taco-fan__emoji';
        emoji.innerText = FLOATING_EMOJIS[Math.floor(Math.random() * FLOATING_EMOJIS.length)];
        emoji.style.setProperty('--target-x', `${(Math.random() - 0.5) * 300}px`);
        emoji.style.setProperty('--target-y', `${-Math.random() * 300 - 50}px`);
        emoji.style.setProperty('--target-rotation', `${(Math.random() - 0.5) * 360}deg`);
        emoji.style.left = `${x}px`;
        emoji.style.top = `${y}px`;
        document.body.appendChild(emoji);
        setTimeout(() => emoji.remove(), 800);
    };

    const spawnTapEffect = (x, y) => {
        const pixel = document.createElement('div');
        pixel.className = 'taco-fan__tap-effect';
        pixel.style.left = `${x}px`;
        pixel.style.top = `${y}px`;
        document.body.appendChild(pixel);
        setTimeout(() => pixel.remove(), 300);
    };

    // ==========================================================================
    // CONTROLADOR PRINCIPAL DEL TAP
    // ==========================================================================
    const handleScreenTap = (event) => {
        if (!canTap) return;

        let tapX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
        let tapY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;

        // 1. Efectos Visuales
        spawnTapEffect(tapX, tapY);
        if (tapY > window.innerHeight / 2) spawnFloatingEmoji(tapX, tapY);

        if (tacoImageRef.current) {
            tacoImageRef.current.style.transform = 'scale(1.15)';
            setTimeout(() => {
                if (tacoImageRef.current && !tacoImageRef.current.classList.contains('taco-fan__image--slide-out')) {
                    tacoImageRef.current.style.transform = 'scale(1)';
                }
            }, 50);
        }

        // 2. Progreso Visual Local Inmediato (Para que no se sienta lag)
        setLocalStep((prev) => {
            if (prev === MAXIMUM_TACO_STEPS) {
                if (tacoImageRef.current) {
                    tacoImageRef.current.classList.add('taco-fan__image--slide-out');
                    setTimeout(() => {
                        if (tacoImageRef.current) {
                            tacoImageRef.current.classList.remove('taco-fan__image--slide-out');
                            tacoImageRef.current.style.transform = 'scale(1)';
                        }
                        setLocalScore(s => s + 1);
                    }, 300);
                }
                return 1;
            }
            return prev + 1;
        });

        // 3. Cola de envíos al servidor
        const buffer = tapBufferRef.current;
        buffer.count++;

        if (buffer.count >= BATCH_SIZE) {
            flushTapsToServer();
            return;
        }

        if (!buffer.timer) {
            buffer.timer = setTimeout(flushTapsToServer, FLUSH_MS);
        }
    };

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    
    // PANTALLA DE SELECCIÓN DE JUGADOR
    if (!playerId) {
        return <SelectPlayer onPick={(p) => { setPlayerId(p); sessionStorage.setItem('taco.player', p); }} />
    }

    const getWinnerMessage = () => {
        if (matchState.winner === 'none') return '';
        if (matchState.winner === 'tie') return '¡ES UN EMPATE!';
        return matchState.winner === playerId ? '¡GANASTE LA RONDA!' : '¡PERDISTE LA RONDA!';
    };

    return (
        <div className="taco-fan" onMouseDown={handleScreenTap} onTouchStart={handleScreenTap}>
            
            {/* CABECERA: Botón de Salir y Estatus */}
            <header style={{ position: 'absolute', top: 0, left: 0, width: '100%', padding: '15px', display: 'flex', flexDirection: 'column', zIndex: 12 }}> {/* justifyContent: 'space-between', */}
                <img src="/img/page/adsl-five.png" className="LogoADSLxZtudia" alt="Logo" />

                <div className='d-flex justify-content-between mt-3'>
                    <button 
                        className="btn btn-danger btn-sm fw-bold" 
                        onClick={() => { setPlayerId(''); sessionStorage.removeItem('taco.player'); }}
                    >
                        <i className="bi bi-box-arrow-right"></i> Salir
                    </button>

                    <div className="text-end">
                        <span className="badge bg-dark small d-block mb-1">{playerId.toUpperCase()}</span>
                        <span className={`badge ${connectionStatus === 'connected' ? 'bg-success' : 'bg-warning'} small d-block`}>
                            {connectionStatus === 'connected' && 'Conectado'}
                        </span>
                    </div>
                </div>
            </header>

            {/* MARCADOR LOCAL */}
            {/* <div style={{ position: 'absolute', top: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, textAlign: 'center' }}> */}
                {/* <p style={{ color: '#f1c40f', fontSize: '2rem', textShadow: '2px 2px 0 #000', margin: 0 }}>
                    {localScore} TACOS
                </p> */}
                {!canTap && matchState.phase !== 'FINISHED' && (
                    <div className='taco-fan__Set'>
                        <p className='taco-fan__Set__Text'>¡Prepárate!</p>
                    </div>
                )}
            {/* </div> */}

            {/* MITAD SUPERIOR (Taco) */}
            <div className="taco-fan__zone">
                <img 
                    ref={tacoImageRef}
                    src={ASSETS.tacoStepTemplate(localStep)} 
                    alt={`Paso ${localStep}`} 
                    className="taco-fan__image" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

            {/* MITAD INFERIOR (Personaje) */}
            <div className="taco-fan__zone">
                <img 
                    src={ASSETS.character} 
                    alt="Taquero" 
                    className="taco-fan__image" 
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
            </div>

            {/* PANTALLA FINAL */}
            {matchState.phase === 'FINISHED' && (
                <div className="taco-fan__overlay">
                    <div className="taco-fan__note">
                        <div className="taco-fan__result-text">
                            <p style={{ color: matchState.winner === playerId ? '#198754' : '#dc3545', fontSize: '1.2rem', marginBottom: '20px' }}>
                                {getWinnerMessage()}
                            </p>
                            {/* <p>Preparaste</p>
                            <p style={{ fontSize: '2.5rem', color: '#e74c3c', margin: '10px 0' }}>{localScore}</p>
                            <p>tacos en total.</p> */}
                        </div>
                    </div>
                </div>
            )}

            {/* ALERTAS */}
            {/* {toastMessage && (
                <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#198754', color: '#fff', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold', zIndex: 100 }}>
                    {toastMessage}
                </div>
            )} */}

            <p className='FooterCredits' style={{ textAlign: 'center', zIndex: '12', color: '#fff', fontSize: '18px' }}>Hecho con ♥ por Five Digital Minds | 2026</p>
        </div>
    );
};

// ==========================================================================
// PANTALLA DE SELECCIÓN (Similar a Tiro a Gol)
// ==========================================================================
const SelectPlayer = ({ onPick }) => {
    return (
        <GoalBase classNameApplied={'Fan__Goal__Header'}>
            <div className="Fan__Goal">
                <p className="Fan__Goal__Title">Selecciona tu taquer@</p>
                <div className="Fan__Goal__Players">
                    <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player1')}>
                        <img src='/img/games/taco/lucho-taquero.png' className='Fan__Goal__Players__Buttons__Image' alt="Lucho" />
                        <span className='Fan__Goal__Players__Buttons__Text'>Lucho</span>
                    </button>
                    <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player2')}>
                        <img src='/img/games/taco/tunita-taquera.png' className='Fan__Goal__Players__Buttons__Image' alt="Tunita" />
                        <span className='Fan__Goal__Players__Buttons__Text'>Tunita</span>
                    </button>
                </div>
            </div>
        </GoalBase>
    );
};

export { Tacos };









// import React, { useEffect, useRef, useState } from 'react';
// import { 
//     ConnectTacoWebSocket, 
//     SetTacoSocketMessageHandler, 
//     ClearTacoSocketMessageHandler, 
//     DisconnectTacoWebSocket, 
//     SendTacoTaps 
// } from '../../../utilities/BL/Tacos';
// import { GoalBase } from '../../Admin/Commons/GoalBase';

// /**
//  * COMPONENTE AFICIONADO: TACO TAP CHALLENGE
//  */
// const Tacos = () => {
//     // ==========================================================================
//     // ESTADOS DE JUGADOR Y RED
//     // ==========================================================================
//     const [playerId, setPlayerId] = useState(() => sessionStorage.getItem('taco.player') || '');
//     const [connectionStatus, setConnectionStatus] = useState('idle');
//     const [toastMessage, setToastMessage] = useState('');

//     // ==========================================================================
//     // ESTADOS DEL JUEGO (Sincronizados con el Servidor)
//     // ==========================================================================
//     const [matchState, setMatchState] = useState({
//         matchId: null,
//         phase: 'WAITING', // WAITING, RUNNING, FINISHED
//         winner: 'none'
//     });

//     // Estados visuales locales (para respuesta inmediata)
//     const [localScore, setLocalScore] = useState(0);
//     const [localStep, setLocalStep] = useState(1);
    
//     // Referencias de alto rendimiento
//     const tacoImageRef = useRef(null);
//     const tapBufferRef = useRef({ count: 0, timer: null, isSending: false });

//     // Configuración
//     const BATCH_SIZE = 5;
//     const FLUSH_MS = 120;
//     const MAXIMUM_TACO_STEPS = 4;
//     const FLOATING_EMOJIS = ['🌮', '🔥', '✨', '👨‍🍳', '🌶️', '🍅'];
//     const ASSETS = {
//         character: '/img/games/taco/lucho-taquero.png',
//         tacoStepTemplate: (stepNumber) => `/img/games/taco/${stepNumber}.png` 
//     };

//     // ==========================================================================
//     // CONEXIÓN WEBSOCKET
//     // ==========================================================================
//     useEffect(() => {
//         if (!playerId) return;

//         const showToast = (m) => { setToastMessage(m); setTimeout(() => setToastMessage(''), 2500); };
        
//         ConnectTacoWebSocket(playerId, setConnectionStatus, showToast).then(() => {
//             SetTacoSocketMessageHandler(handleIncomingMessage);
//         });

//         return () => {
//             ClearTacoSocketMessageHandler();
//             DisconnectTacoWebSocket();
//             if (tapBufferRef.current.timer) clearTimeout(tapBufferRef.current.timer);
//         };
//     }, [playerId]);

//     const handleIncomingMessage = (msg) => {
//         const type = (msg?.type || '').toLowerCase();
//         const payload = msg?.payload || {};

//         if (type === 'taco.progress') {
//             setMatchState(prev => ({
//                 ...prev,
//                 matchId: payload.matchId,
//                 phase: payload.status === 1 ? 'RUNNING' : payload.status === 2 ? 'FINISHED' : 'WAITING',
//                 winner: payload.winner
//             }));

//             // Sincronizamos la UI local con la verdad del servidor
//             const myStats = playerId === 'player1' ? payload.player1 : payload.player2;
//             if (myStats) {
//                 setLocalScore(myStats.tacosCompleted);
//                 setLocalStep(myStats.currentStep);
//             }
//         } else if (type === 'taco.finished') {
//             setMatchState(prev => ({
//                 ...prev,
//                 phase: 'FINISHED',
//                 winner: payload.winner
//             }));
//         }
//     };

//     // ==========================================================================
//     // SISTEMA DE BATCHING (Envío optimizado de Taps)
//     // ==========================================================================
//     const canTap = Boolean(playerId && matchState.matchId && matchState.phase === 'RUNNING' && matchState.winner === 'none');

//     const flushTapsToServer = async () => {
//         const buffer = tapBufferRef.current;
//         if (buffer.timer) { clearTimeout(buffer.timer); buffer.timer = null; }
//         if (buffer.isSending || buffer.count <= 0 || !matchState.matchId) return;

//         const tapsToSend = buffer.count;
//         buffer.count = 0;
//         buffer.isSending = true;

//         try {
//             await SendTacoTaps({ matchId: matchState.matchId, playerId, taps: tapsToSend });
//         } catch {
//             buffer.count += tapsToSend; // Si falla, los regresamos a la cola
//         } finally {
//             buffer.isSending = false;
//         }
//     };

//     // ==========================================================================
//     // LÓGICA DE PARTÍCULAS VISUALES (DOM Directo)
//     // ==========================================================================
//     const spawnFloatingEmoji = (x, y) => {
//         const emoji = document.createElement('div');
//         emoji.className = 'taco-fan__emoji';
//         emoji.innerText = FLOATING_EMOJIS[Math.floor(Math.random() * FLOATING_EMOJIS.length)];
//         emoji.style.setProperty('--target-x', `${(Math.random() - 0.5) * 300}px`);
//         emoji.style.setProperty('--target-y', `${-Math.random() * 300 - 50}px`);
//         emoji.style.setProperty('--target-rotation', `${(Math.random() - 0.5) * 360}deg`);
//         emoji.style.left = `${x}px`;
//         emoji.style.top = `${y}px`;
//         document.body.appendChild(emoji);
//         setTimeout(() => emoji.remove(), 800);
//     };

//     const spawnTapEffect = (x, y) => {
//         const pixel = document.createElement('div');
//         pixel.className = 'taco-fan__tap-effect';
//         pixel.style.left = `${x}px`;
//         pixel.style.top = `${y}px`;
//         document.body.appendChild(pixel);
//         setTimeout(() => pixel.remove(), 300);
//     };

//     // ==========================================================================
//     // CONTROLADOR PRINCIPAL DEL TAP
//     // ==========================================================================
//     const handleScreenTap = (event) => {
//         if (!canTap) return;

//         let tapX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
//         let tapY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;

//         // 1. Efectos Visuales
//         spawnTapEffect(tapX, tapY);
//         if (tapY > window.innerHeight / 2) spawnFloatingEmoji(tapX, tapY);

//         if (tacoImageRef.current) {
//             tacoImageRef.current.style.transform = 'scale(1.15)';
//             setTimeout(() => {
//                 if (tacoImageRef.current && !tacoImageRef.current.classList.contains('taco-fan__image--slide-out')) {
//                     tacoImageRef.current.style.transform = 'scale(1)';
//                 }
//             }, 50);
//         }

//         // 2. Progreso Visual Local Inmediato (Para que no se sienta lag)
//         setLocalStep((prev) => {
//             if (prev === MAXIMUM_TACO_STEPS) {
//                 if (tacoImageRef.current) {
//                     tacoImageRef.current.classList.add('taco-fan__image--slide-out');
//                     setTimeout(() => {
//                         if (tacoImageRef.current) {
//                             tacoImageRef.current.classList.remove('taco-fan__image--slide-out');
//                             tacoImageRef.current.style.transform = 'scale(1)';
//                         }
//                         setLocalScore(s => s + 1);
//                     }, 300);
//                 }
//                 return 1;
//             }
//             return prev + 1;
//         });

//         // 3. Cola de envíos al servidor
//         const buffer = tapBufferRef.current;
//         buffer.count++;

//         if (buffer.count >= BATCH_SIZE) {
//             flushTapsToServer();
//             return;
//         }

//         if (!buffer.timer) {
//             buffer.timer = setTimeout(flushTapsToServer, FLUSH_MS);
//         }
//     };

//     // ==========================================================================
//     // RENDERIZADO
//     // ==========================================================================
    
//     // PANTALLA DE SELECCIÓN DE JUGADOR
//     if (!playerId) {
//         return <SelectPlayer onPick={(p) => { setPlayerId(p); sessionStorage.setItem('taco.player', p); }} />
//     }

//     const getWinnerMessage = () => {
//         if (matchState.winner === 'none') return '';
//         if (matchState.winner === 'tie') return '¡ES UN EMPATE!';
//         return matchState.winner === playerId ? '¡GANASTE LA RONDA!' : '¡PERDISTE LA RONDA!';
//     };

//     return (
//         <div className="taco-fan" onMouseDown={handleScreenTap} onTouchStart={handleScreenTap}>
            
//             {/* CABECERA: Botón de Salir y Estatus */}
//             <header style={{ position: 'absolute', top: 0, left: 0, width: '100%', padding: '15px', display: 'flex', justifyContent: 'space-between', zIndex: 50 }}>
//                 <button 
//                     className="btn btn-danger btn-sm fw-bold" 
//                     onClick={() => { setPlayerId(''); sessionStorage.removeItem('taco.player'); }}
//                 >
//                     <i className="bi bi-box-arrow-right"></i> Salir
//                 </button>
//                 <div className="text-end">
//                     <span className="badge bg-dark fs-6 d-block mb-1">{playerId.toUpperCase()}</span>
//                     <span className={`badge ${connectionStatus === 'connected' ? 'bg-success' : 'bg-warning'} fs-6 d-block`}>
//                         {connectionStatus}
//                     </span>
//                 </div>
//             </header>

//             {/* MARCADOR LOCAL */}
//             <div style={{ position: 'absolute', top: '80px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, textAlign: 'center' }}>
//                 <p style={{ color: '#f1c40f', fontSize: '2rem', textShadow: '2px 2px 0 #000', margin: 0 }}>
//                     {localScore} TACOS
//                 </p>
//                 {!canTap && matchState.phase !== 'FINISHED' && (
//                     <p style={{ color: '#fff', fontSize: '1rem', marginTop: '10px' }}>¡Prepárate!</p>
//                 )}
//             </div>

//             {/* MITAD SUPERIOR (Taco) */}
//             <div className="taco-fan__zone">
//                 <img 
//                     ref={tacoImageRef}
//                     src={ASSETS.tacoStepTemplate(localStep)} 
//                     alt={`Paso ${localStep}`} 
//                     className="taco-fan__image" 
//                     onError={(e) => { e.target.style.display = 'none'; }}
//                 />
//             </div>

//             {/* MITAD INFERIOR (Personaje) */}
//             <div className="taco-fan__zone">
//                 <img 
//                     src={ASSETS.character} 
//                     alt="Taquero" 
//                     className="taco-fan__image" 
//                     onError={(e) => { e.target.style.display = 'none'; }}
//                 />
//             </div>

//             {/* PANTALLA FINAL */}
//             {matchState.phase === 'FINISHED' && (
//                 <div className="taco-fan__overlay">
//                     <div className="taco-fan__note">
//                         <div className="taco-fan__result-text">
//                             <p style={{ color: matchState.winner === playerId ? '#198754' : '#dc3545', fontSize: '1.2rem', marginBottom: '20px' }}>
//                                 {getWinnerMessage()}
//                             </p>
//                             <p>Preparaste</p>
//                             <p style={{ fontSize: '2.5rem', color: '#e74c3c', margin: '10px 0' }}>{localScore}</p>
//                             <p>tacos en total.</p>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* ALERTAS */}
//             {toastMessage && (
//                 <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#198754', color: '#fff', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold', zIndex: 100 }}>
//                     {toastMessage}
//                 </div>
//             )}
//         </div>
//     );
// };

// // ==========================================================================
// // PANTALLA DE SELECCIÓN (Similar a Tiro a Gol)
// // ==========================================================================
// const SelectPlayer = ({ onPick }) => {
//     return (
//         <GoalBase classNameApplied={'Fan__Goal__Header'}>
//             <div className="Fan__Goal">
//                 <p className="Fan__Goal__Title">Selecciona tu Taquero</p>
//                 <div className="Fan__Goal__Players">
//                     <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player1')}>
//                         <img src='/img/games/taco/lucho-taquero.png' className='Fan__Goal__Players__Buttons__Image' alt="Lucho" />
//                         <span className='Fan__Goal__Players__Buttons__Text'>Lucho</span>
//                     </button>
//                     <button className="Fan__Goal__Players__Buttons" onClick={() => onPick('player2')}>
//                         <img src='/img/games/taco/tunita-taquera.png' className='Fan__Goal__Players__Buttons__Image' alt="Tunita" />
//                         <span className='Fan__Goal__Players__Buttons__Text'>Tunita</span>
//                     </button>
//                 </div>
//             </div>
//         </GoalBase>
//     );
// };

// export { Tacos };









// import React, { useEffect, useRef, useState } from 'react';

// /**
//  * COMPONENTE AFICIONADO: TACO TAP CHALLENGE
//  * Juego de velocidad 1v1 donde el usuario arma tacos con taps.
//  */
// const Tacos = () => {
//     // ==========================================================================
//     // PARÁMETROS DE CONFIGURACIÓN DEL JUEGO
//     // ==========================================================================
//     const REQUIRED_TAPS_PER_STEP = 1;
//     const MAXIMUM_TACO_STEPS = 4;
//     const INITIAL_GAME_TIME_SECONDS = 20;
//     const FLOATING_EMOJIS = ['🌮', '🔥', '✨', '👨‍🍳', '🌶️', '🍅'];

//     // ==========================================================================
//     // ESTADOS Y REFERENCIAS
//     // ==========================================================================
//     // Estados visuales controlados por React
//     const [timeLeftInSeconds, setTimeLeftInSeconds] = useState(INITIAL_GAME_TIME_SECONDS);
//     const [totalTacosPrepared, setTotalTacosPrepared] = useState(0);
//     const [currentTacoStep, setCurrentTacoStep] = useState(1);
//     const [isGameActive, setIsGameActive] = useState(true);

//     // Referencias mutables para lógica de alto rendimiento (evita re-renders en cada tap)
//     const currentTapsAccumulatedRef = useRef(0);
//     const tacoImageRef = useRef(null); // Referencia a la imagen del taco para animarla directamente

//     // Rutas de imágenes
//     const ASSETS = {
//         character: '/img/games/taco/personaje.png',
//         // Generamos la ruta dinámicamente según el paso actual (1.png, 2.png, etc.)
//         tacoStepTemplate: (stepNumber) => `/img/games/taco/${stepNumber}.png` 
//     };

//     // ==========================================================================
//     // TEMPORIZADOR DEL JUEGO
//     // ==========================================================================
//     useEffect(() => {
//         if (!isGameActive) return;

//         const countdownInterval = setInterval(() => {
//             setTimeLeftInSeconds((previousTime) => {
//                 if (previousTime <= 1) {
//                     clearInterval(countdownInterval);
//                     setIsGameActive(false); // Termina el juego al llegar a 0
//                     return 0;
//                 }
//                 return previousTime - 1;
//             });
//         }, 1000);

//         // Limpieza del intervalo cuando el componente se desmonta
//         return () => clearInterval(countdownInterval);
//     }, [isGameActive]);

//     // ==========================================================================
//     // LÓGICA DE PARTÍCULAS Y EMOJIS (Inyección directa al DOM para rendimiento)
//     // ==========================================================================
//     const spawnFloatingEmoji = (coordinateX, coordinateY) => {
//         const emojiElement = document.createElement('div');
//         emojiElement.className = 'taco-fan__emoji';
        
//         // Seleccionamos un emoji al azar de nuestra lista
//         const randomEmojiIndex = Math.floor(Math.random() * FLOATING_EMOJIS.length);
//         emojiElement.innerText = FLOATING_EMOJIS[randomEmojiIndex];
        
//         // Calculamos una trayectoria de vuelo aleatoria
//         const targetX = (Math.random() - 0.5) * 300; 
//         const targetY = -Math.random() * 300 - 50;   
//         const targetRotation = (Math.random() - 0.5) * 360; 

//         // Asignamos variables CSS personalizadas para la animación
//         emojiElement.style.setProperty('--target-x', `${targetX}px`);
//         emojiElement.style.setProperty('--target-y', `${targetY}px`);
//         emojiElement.style.setProperty('--target-rotation', `${targetRotation}deg`);
        
//         emojiElement.style.left = `${coordinateX}px`;
//         emojiElement.style.top = `${coordinateY}px`;
        
//         document.body.appendChild(emojiElement);
        
//         // Limpiamos el DOM después de que termine la animación
//         setTimeout(() => emojiElement.remove(), 800);
//     };

//     const spawnTapEffect = (coordinateX, coordinateY) => {
//         const tapPixelElement = document.createElement('div');
//         tapPixelElement.className = 'taco-fan__tap-effect';
//         tapPixelElement.style.left = `${coordinateX}px`;
//         tapPixelElement.style.top = `${coordinateY}px`;
        
//         document.body.appendChild(tapPixelElement);
        
//         // Limpiamos el DOM después de la animación de "latido pixelado"
//         setTimeout(() => tapPixelElement.remove(), 300);
//     };

//     // ==========================================================================
//     // CONTROLADOR PRINCIPAL DEL TAP
//     // ==========================================================================
//     const handleScreenTap = (event) => {
//         if (!isGameActive) return;

//         // Obtenemos coordenadas (soporta tanto mouse como touch de celular)
//         let tapX = 0;
//         let tapY = 0;

//         if (event.type === 'touchstart') {
//             tapX = event.touches[0].clientX;
//             tapY = event.touches[0].clientY;
//         } else {
//             tapX = event.clientX;
//             tapY = event.clientY;
//         }

//         // 1. DIBUJAR EFECTOS VISUALES
//         spawnTapEffect(tapX, tapY);

//         // Si el tap fue en la mitad inferior de la pantalla, soltamos emojis
//         const isBottomHalfOfScreen = tapY > window.innerHeight / 2;
//         if (isBottomHalfOfScreen) {
//             spawnFloatingEmoji(tapX, tapY);
//         }

//         // 2. ANIMAR EL TACO (Latido)
//         if (tacoImageRef.current) {
//             tacoImageRef.current.style.transform = 'scale(1.15)';
//             setTimeout(() => {
//                 // Solo regresamos a tamaño normal si no está ejecutando la animación de salida
//                 if (tacoImageRef.current && !tacoImageRef.current.classList.contains('taco-fan__image--slide-out')) {
//                     tacoImageRef.current.style.transform = 'scale(1)';
//                 }
//             }, 50);
//         }

//         // 3. LÓGICA DE PROGRESO DEL TACO
//         currentTapsAccumulatedRef.current += 1;

//         if (currentTapsAccumulatedRef.current >= REQUIRED_TAPS_PER_STEP) {
//             currentTapsAccumulatedRef.current = 0; // Reiniciamos contador del paso

//             // Leemos el estado actual a través de la función setter para evitar condiciones de carrera
//             setCurrentTacoStep((previousStep) => {
//                 if (previousStep === MAXIMUM_TACO_STEPS) {
//                     // Si ya terminamos el taco, activamos la animación de salida
//                     if (tacoImageRef.current) {
//                         tacoImageRef.current.classList.add('taco-fan__image--slide-out');
                        
//                         setTimeout(() => {
//                             if (tacoImageRef.current) {
//                                 tacoImageRef.current.classList.remove('taco-fan__image--slide-out');
//                                 tacoImageRef.current.style.transform = 'scale(1)';
//                             }
//                             // Sumamos el punto y reseteamos el taco al paso 1
//                             setTotalTacosPrepared((prevScore) => prevScore + 1);
//                             setCurrentTacoStep(1);
//                         }, 300); // 300ms es lo que dura la animación CSS
//                     }
//                     return previousStep; // Retornamos el mismo paso mientras se anima
//                 } else {
//                     // Si no hemos terminado, pasamos a la siguiente imagen (ej. de tortilla a tortilla con carne)
//                     return previousStep + 1;
//                 }
//             });
//         }
//     };

//     // ==========================================================================
//     // RENDERIZADO DEL COMPONENTE
//     // ==========================================================================
//     return (
//         <div 
//             className="taco-fan" 
//             onMouseDown={handleScreenTap} 
//             onTouchStart={handleScreenTap}
//         >
//             {/* TIEMPO RESTANTE (Solo para referencia visual durante desarrollo) */}
//             <div style={{ position: 'absolute', top: '10px', left: '10px', color: '#fff', zIndex: 10 }}>
//                 {timeLeftInSeconds}s
//             </div>

//             {/* MITAD SUPERIOR (El Taco que se va armando) */}
//             <div className="taco-fan__zone">
//                 <img 
//                     ref={tacoImageRef}
//                     src={ASSETS.tacoStepTemplate(currentTacoStep)} 
//                     alt={`Taco paso ${currentTacoStep}`} 
//                     className="taco-fan__image" 
//                     onError={(e) => { e.target.style.display = 'none'; }} // Si no encuentra la imagen, la oculta
//                 />
//             </div>

//             {/* MITAD INFERIOR (El Personaje) */}
//             <div className="taco-fan__zone">
//                 <img 
//                     src={ASSETS.character} 
//                     alt="Personaje Taquero" 
//                     className="taco-fan__image" 
//                     onError={(e) => { e.target.style.display = 'none'; }}
//                 />
//             </div>

//             {/* PANTALLA FINAL (Cuando se acaba el tiempo) */}
//             {!isGameActive && (
//                 <div className="taco-fan__overlay">
//                     <div className="taco-fan__note">
//                         <div className="taco-fan__result-text">
//                             <p>¡TIEMPO AGOTADO!</p>
//                             <p>Preparaste</p>
//                             <p style={{ fontSize: '1.5rem', color: '#e74c3c' }}>{totalTacosPrepared}</p>
//                             <p>tacos</p>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export { Tacos };